import React from 'react';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import type { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import type { ChartDropdown } from '../../../pages/Summary/Components/SummaryGraphContainer.tsx';
import { useDispatch } from 'react-redux';
import { setSelectedViewAllChart } from '../../../redux/slices/summaryGraphViewSlice.ts';
import './GraphViewControlBar.scss';
import type { ChartType } from '../../../pages/Summary/Components/Charts/ChartTypes.ts';
import { useMediaQuery, useTheme } from '@mui/material';

export interface GraphViewOption {
	id: string;
	label: string;
}

export interface GraphViewSelectorProps {
	/**
	 * Whether to display the 'View All' link
	 * @default false
	 */
	viewAll?: boolean;

	/**
	 * Optional dropdown configuration for selecting graph views
	 * @default if not provided, no dropdown will be shown
	 */
	dropdown?: ChartDropdown;

	chartType?: ChartType | null;
}

/**
 * GraphViewControlBar - A component for switching between different graph visualization types
 */
const GraphViewControlBar: React.FC<GraphViewSelectorProps> = ({ viewAll = false, dropdown, chartType = null }) => {
	const dispatch = useDispatch();
	const handleViewChange = (event: SelectChangeEvent<string>) => {
		const newView = event.target.value as string;

		// Find the selected option
		const selectedOption = dropdown?.options.find(
			(option: GraphViewOption) => option.id === newView
		);

		if (selectedOption && dropdown?.onChange) {
			dropdown.onChange({
				label: selectedOption.label,
				id: selectedOption.id,
			});
		}
	};
  	
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));

	viewAll = isMobile ? false : viewAll;
	
	const handleViewAllClick = () => {
		dispatch(setSelectedViewAllChart(chartType || null));
	}

	return (
		<Box
			data-testid="graph-view-selector"
			sx={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
				backgroundColor: (theme) => theme.palette.surface.secondary,
				px: 2,
				py: 1,
				mb: 2,
				borderBottom: '1px solid',
				borderColor: (theme) => theme.palette.divider,
			}}
		>
			<Box sx={{ display: 'flex', alignItems: 'center', height: 25 }}>
				{dropdown?.options && (
					<FormControl size="small" sx={{ minWidth: 150, mr: 2 }} id="graph-view-select">
						<Select
							variant="standard"
							labelId="graph-view-select-label"
							data-testid="graph-view-select"
							value={dropdown?.selection?.id}
							onChange={handleViewChange}
							MenuProps={{ disablePortal: true }}
						>
							{dropdown?.options.map((option) => (
								<MenuItem key={option.id} value={option.id}>
									{option.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				)}
			</Box>
			{viewAll && 
				<Box onClick={handleViewAllClick} className="graph-view-all-link" data-testid="graph-view-all-link">
					View All
				</Box>}
		</Box>
	);
};

export default GraphViewControlBar;
